/**
 * What insurance actually costs on a job, at the rates the company is charged.
 *
 * Both policies are rated on what the crew is paid, not on what the customer
 * pays, and the rate depends on two things: what trade the work is, and
 * whether the crew carries its own insurance. An insured roofer costs 0.7% of
 * their work order in general liability and nothing in workers comp; the same
 * work given to an uninsured crew costs eighteen times that in liability and
 * another 7.35% in comp. That difference is the whole reason this file exists
 * — a single blended percentage cannot express it, and the company was using
 * one.
 *
 * ## Where the numbers come from
 *
 * General liability: policy BAS-83535-1, Crum & Forster Specialty Insurance
 * Company, 09/02/2026 to 09/02/2027, forms SB049-0615 and SB071-0615. Every
 * rate below is a class rate off those two declarations pages, quoted per
 * $1,000 of what the crew is paid. Renewal replaces them.
 *
 * Workers comp: the Greater Birmingham Association of Home Builders fund,
 * quoted per $100 of payroll and reported monthly. The roofing rate is $11.32
 * less a 35% discount; Lysa wrote the result down as $7.35 and the agent
 * confirmed it in the binding meeting on 2026-09-01. The gutter rate is her
 * $5.85, which the agent explicitly would not stand behind — see
 * `GUTTER_WC_UNCONFIRMED`.
 *
 * ## The two per-hundred / per-thousand traps
 *
 * Liability is quoted per $1,000 and comp per $100, for no reason anybody
 * could give. A rate written into the wrong one is out by a factor of ten and
 * still looks plausible, so the constants below carry their basis in the name
 * and nothing here reads a bare number.
 *
 * The second trap is the audit. These rates are charged against what the
 * company really paid out over the year, not against the estimate the policy
 * was written on. Budgeting them per job is the only way the money is there
 * when the audit lands.
 */

import { type Cents, type Percent, percentOf, toPercent } from "./money.js";
import { type CrewPapers, policyState } from "./crewPapers.js";

/**
 * One rating class: what the carrier calls this kind of work, and what it
 * charges for it.
 *
 * `glPer1000` is charged whoever does the work; `wcPer100` only when the
 * company carries the comp. A class with no comp rate on file charges the
 * roofing rate, which is the highest — see `wcRateFor`.
 */
export interface InsuranceClass {
  /** The classification as the declarations page names it. */
  name: string;
  /** The carrier's class code, so a rate can be checked against the policy. */
  code: string;
  /** General liability, dollars per $1,000 paid. */
  glPer1000: number;
  /** Workers comp, dollars per $100 of payroll, after the 35% discount. */
  wcPer100: number | null;
}

/**
 * What a subcontractor with a current certificate costs, whatever they do.
 *
 * Class 91583, "Contractors - subcontracted work". The agent's words:
 * "as long as they've got insurance, the rate is six dollars and ninety-eight
 * cents" — roofing, painting, siding, all of it. This one rate is why chasing
 * a certificate is worth real money.
 */
export const INSURED_SUB_GL_PER_1000 = 6.98;

/** The workers comp discount the fund applies to every rate. */
export const WC_DISCOUNT_PCT = 35;

/**
 * Lysa's gutter comp rate, which the agent would not confirm.
 *
 * "Don't hold me to that. I just make rough numbers. I know the roofing number
 * is right. I don't know about the gutter number." Kept because a number
 * somebody wrote down beats no number at all, and marked so the day it is
 * checked nobody has to work out which figure was the guess.
 */
export const GUTTER_WC_UNCONFIRMED = true;

const ROOFING: InsuranceClass = {
  name: "Roofing - residential",
  code: "98678",
  glPer1000: 125.35,
  // $11.32 less the 35% discount is $7.358. Lysa's written figure.
  wcPer100: 7.35,
};

const GUTTERS: InsuranceClass = {
  name: "Gutter Installation",
  code: "99984",
  glPer1000: 25.97,
  wcPer100: 5.85,
};

const PAINTING: InsuranceClass = {
  name: "Painting - interior",
  code: "98305",
  glPer1000: 18.67,
  wcPer100: null,
};

const DRYWALL: InsuranceClass = {
  name: "Dry Wall or Wall Board Installation",
  code: "92338",
  glPer1000: 19.27,
  wcPer100: null,
};

/**
 * Decks, siding and siding repairs. Jonathon gave the rate on 2026-10-09
 * without the carrier's class code, so the code here is ours, not theirs —
 * it only has to be distinct, because lines are grouped by it. Swap in the
 * carrier's code when the endorsement is in hand.
 */
const EXTERIOR_CARPENTRY: InsuranceClass = {
  name: "Carpentry - exterior",
  code: "exterior-carpentry",
  glPer1000: 49.15,
  wcPer100: null,
};

/**
 * Which class a trade falls in.
 *
 * Matched on a word rather than the whole name, because a trade is free text
 * on the document (rule 8) and "Gutters" today may be "Gutter Replacement"
 * tomorrow. Anything unrecognised is roofing: roofing is the dearest rate on
 * the policy, and a job that budgets too much insurance costs the company
 * nothing at the audit.
 *
 * "All Other Repairs" is painting — Jonathon's call on 2026-10-09, between
 * painting and drywall (ADR 0278). It is matched on "other repair", never on
 * "repair", which would catch "Roofing Repairs" and "Siding Repairs" too.
 */
const CLASS_KEYWORDS: readonly (readonly [string, InsuranceClass])[] = [
  ["gutter", GUTTERS],
  ["siding", EXTERIOR_CARPENTRY],
  ["deck", EXTERIOR_CARPENTRY],
  ["carpentry", EXTERIOR_CARPENTRY],
  ["paint", PAINTING],
  ["drywall", DRYWALL],
  ["dry wall", DRYWALL],
  ["other repair", PAINTING],
];

/**
 * Every liability class, in the order Jonathon listed the rates on
 * 2026-10-13, so the monthly email can show what each payout was charged
 * (ADR 0275).
 */
export const LIABILITY_CLASSES: readonly InsuranceClass[] = [
  ROOFING,
  GUTTERS,
  EXTERIOR_CARPENTRY,
  DRYWALL,
  PAINTING,
];

export function insuranceClassFor(
  trade: string | null | undefined,
): InsuranceClass {
  const name = (trade ?? "").toLowerCase();
  for (const [keyword, klass] of CLASS_KEYWORDS) {
    if (name.includes(keyword)) return klass;
  }
  return ROOFING;
}

/**
 * The trades on a work order, however the column happens to hold them.
 *
 * `work_orders.trades` is `text[]` today and was a comma-separated string in
 * the prototype, and rows written then were never converted.
 */
export type TradeList =
  readonly (string | null | undefined)[] | string | null | undefined;

export function tradeList(trades: TradeList): string[] {
  if (typeof trades === "string") {
    return trades
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  }
  return (trades ?? [])
    .map((t) => (t ?? "").trim())
    .filter((t) => t.length > 0);
}

/**
 * The class a work order is rated in when it covers several trades.
 *
 * The dearest one. A single order for a roof and its gutters is rated as a
 * roof, which is what the carrier would do with it and what stops a cheap
 * trade on the order quietly cutting the budget for an expensive one.
 */
export function insuranceClassForTrades(trades: TradeList): InsuranceClass {
  const list = tradeList(trades);
  if (list.length === 0) return ROOFING;
  return list
    .map(insuranceClassFor)
    .reduce((dearest, klass) =>
      klass.glPer1000 > dearest.glPer1000 ? klass : dearest,
    );
}

/** Comp for a class, falling back to roofing where the fund gave no rate. */
export function wcRateFor(klass: InsuranceClass): number {
  return klass.wcPer100 ?? (ROOFING.wcPer100 as number);
}

/**
 * The comp rate for an order covering several trades: the dearest of them.
 *
 * Asked separately from the liability class on purpose. The two rates are not
 * ordered the same way — painting is the cheaper class on liability and the
 * dearer on comp, because the fund gave no painting rate at all and roofing is
 * what stands in for it. So taking the comp rate off whichever class won on
 * liability lets a gutters-and-painting order budget comp at the gutter rate,
 * which is the cheap trade cutting the budget for the expensive one — the very
 * thing `insuranceClassForTrades` picks the dearest class to prevent.
 */
export function wcRateForTrades(trades: TradeList): number {
  return wcClassForTrades(trades).wcPer100 as number;
}

/**
 * The class whose comp rate `wcRateForTrades` charges, so the line that
 * explains a payout can name it: roofing wherever it stands in for a class
 * the fund gave no rate, and never the liability class just because that one
 * won on liability (issue #919).
 */
export function wcClassForTrades(trades: TradeList): InsuranceClass {
  const list = tradeList(trades);
  if (list.length === 0) return ROOFING;
  return list
    .map((trade) => {
      const klass = insuranceClassFor(trade);
      return klass.wcPer100 == null ? ROOFING : klass;
    })
    .reduce((dearest, klass) =>
      (klass.wcPer100 as number) > (dearest.wcPer100 as number)
        ? klass
        : dearest,
    );
}

/** What the company is charged for a crew, and why. */
export type InsuranceStanding =
  /** A current certificate is on file. Cheap liability, no comp. */
  | "insured"
  /** Somebody has recorded that they carry nothing, or they are our own. */
  | "uninsured"
  /**
   * Nothing on file, or a certificate that has run out.
   *
   * Charged as **uninsured** — Jonathon, 2026-09-22: "if it doesn't know if
   * the crew has it or not i want it to assume that it doesn't." Kept as its
   * own standing rather than folded into `uninsured` so the row can still say
   * the paperwork is merely missing, and that producing a certificate drops
   * the charge.
   *
   * It was the other way round from 2026-09-05 to 2026-09-22 (ADR 0107): no
   * certificate was budgeted as insured and warned about. That budgeted 0.7%
   * of the labour for a sub the audit would charge 12.5% and 7.35% on, and
   * most of the crew list has no certificate on file at all — so the warning
   * was on the majority of jobs and the money was never there. Budgeting too
   * much costs the company nothing at the audit; budgeting too little costs
   * it the difference. See ADR 0198.
   */
  | "assumed";

/** The crew as far as insurance is concerned. */
export interface InsuredCrew extends CrewPapers {
  name?: string | null;
  contact_name?: string | null;
  /** Our own people. No certificate is possible, so every rate is ours. */
  is_in_house?: boolean | null;
}

/**
 * Where a crew stands on one of the two policies.
 *
 * `covered` and `expiring` both count as covered: a certificate that runs out
 * in three weeks is current today, and the warning it deserves belongs on the
 * crew screen, not on the price of a job.
 */
function standing(
  has: boolean | null | undefined,
  expiresOn: string | null | undefined,
  today: string,
): InsuranceStanding {
  const state = policyState(has, expiresOn, today).state;
  if (state === "covered" || state === "expiring") return "insured";
  if (state === "uncovered") return "uninsured";
  return "assumed";
}

export function generalLiabilityStanding(
  crew: InsuredCrew | null | undefined,
  today: string,
): InsuranceStanding {
  if (!crew) return "assumed";
  if (crew.is_in_house === true) return "uninsured";
  return standing(crew.has_gl, crew.gl_expires_on, today);
}

export function workersCompStanding(
  crew: InsuredCrew | null | undefined,
  today: string,
): InsuranceStanding {
  if (!crew) return "assumed";
  if (crew.is_in_house === true) return "uninsured";
  return standing(crew.has_wc, crew.wc_expires_on, today);
}

/** A rate per $1,000, as a percentage of the amount paid. */
export const perThousand = (rate: number): Percent => toPercent(rate / 10);
/** A rate per $100, as a percentage of the amount paid. */
export const perHundred = (rate: number): Percent => toPercent(rate);

/** What one work order costs to insure. */
export interface OrderInsurance {
  klass: InsuranceClass;
  generalLiability: Cents;
  workersComp: Cents;
  glStanding: InsuranceStanding;
  wcStanding: InsuranceStanding;
  /** The rate charged, for the line that explains the number. */
  glPer1000: number;
  wcPer100: number;
}

export interface OrderInsuranceInput {
  /** What the crew is paid — the work order total. */
  paid: Cents;
  trades: TradeList;
  crew: InsuredCrew | null | undefined;
  /** `YYYY-MM-DD`, for reading the certificates against. */
  today: string;
}

/**
 * Liability and comp on one work order.
 *
 * Both are charged on the same base — what the crew is paid — and both are
 * zero on an order worth nothing, which keeps a draft with an empty work order
 * from carrying insurance for work nobody has priced.
 */
export function orderInsurance(input: OrderInsuranceInput): OrderInsurance {
  const klass = insuranceClassForTrades(input.trades);
  const glStanding = generalLiabilityStanding(input.crew, input.today);
  const wcStanding = workersCompStanding(input.crew, input.today);

  // `=== "insured"`, not `!== "uninsured"`: only a certificate we have seen
  // buys the cheap rate. Nothing on file, a lapsed certificate and a crew
  // nobody has chosen yet are all charged at the class rate (ADR 0198).
  const glPer1000 =
    glStanding === "insured" ? INSURED_SUB_GL_PER_1000 : klass.glPer1000;
  // Not `wcRateFor(klass)`: the class is the dearest on liability, which is
  // not always the dearest on comp. See `wcRateForTrades`.
  const wcPer100 = wcStanding === "insured" ? 0 : wcRateForTrades(input.trades);

  const paid = input.paid > 0 ? input.paid : (0 as Cents);

  return {
    klass,
    glStanding,
    wcStanding,
    glPer1000,
    wcPer100,
    generalLiability: percentOf(paid, perThousand(glPer1000)),
    workersComp: percentOf(paid, perHundred(wcPer100)),
  };
}
